import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Crown, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 20;

export default function PaymentResult({ cancel = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { checkAuth } = useAuth();
  const [state, setState] = useState(cancel ? "cancelled" : "checking"); // checking | success | error | cancelled
  const pollsRef = useRef(0);

  useEffect(() => {
    if (cancel || !sessionId) {
      if (!cancel) setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await checkAuth();
          setState("success");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch { /* keep polling */ }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) {
        setState("error");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancel, checkAuth]);

  return (
    <section className="bg-brand-offwhite" data-testid="payment-result-page">
      <div className="container-rv flex min-h-[60vh] items-center justify-center py-24">
        <div className="w-full max-w-md rounded-3xl border border-brand-silver/40 bg-white p-10 text-center shadow-[0_40px_90px_-45px_rgba(26,28,30,0.4)]">
          {state === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand-red" />
              <h1 className="mt-6 font-serif text-2xl font-bold text-brand-charcoal">Confirming your payment…</h1>
              <p className="mt-2 text-sm text-brand-muted">One moment while we activate your membership.</p>
            </div>
          )}

          {state === "success" && (
            <div data-testid="payment-success">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <p className="mt-6 flex items-center justify-center gap-2 overline text-brand-red"><Crown className="h-4 w-4" /> Reverence Member</p>
              <h1 className="mt-2 font-serif text-3xl font-bold text-brand-charcoal">You're a Member!</h1>
              <p className="mt-3 text-sm text-brand-muted">
                Payment received — your membership is active. You now get 10% off every detail, a 50% birthday wash,
                priority scheduling and partner discounts. A receipt is on its way to your inbox.
              </p>
              <Link to="/dashboard" data-testid="payment-success-dashboard" className="btn-red mt-8 w-full">
                Go to my dashboard <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {state === "cancelled" && (
            <div data-testid="payment-cancelled">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-brand-offwhite text-brand-muted">
                <XCircle className="h-8 w-8" />
              </div>
              <h1 className="mt-6 font-serif text-2xl font-bold text-brand-charcoal">Checkout cancelled</h1>
              <p className="mt-3 text-sm text-brand-muted">No charge was made. You can join anytime from your dashboard.</p>
              <Link to="/dashboard" className="btn-red mt-8 w-full" data-testid="payment-cancel-dashboard">Back to dashboard</Link>
            </div>
          )}

          {state === "error" && (
            <div data-testid="payment-error">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-brand-red/10 text-brand-red">
                <XCircle className="h-8 w-8" />
              </div>
              <h1 className="mt-6 font-serif text-2xl font-bold text-brand-charcoal">We couldn't confirm your payment</h1>
              <p className="mt-3 text-sm text-brand-muted">
                If you were charged, your membership will activate shortly. Otherwise, please try again from your dashboard.
              </p>
              <Link to="/dashboard" className="btn-red mt-8 w-full">Back to dashboard</Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
